import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const LEAVE_TYPES = { casual: 'Casual Leave', sick: 'Sick Leave', paid: 'Paid Leave', unpaid: 'Unpaid Leave' };
const TYPE_COLORS = { casual: 'pill-blue', sick: 'pill-orange', paid: 'pill-green', unpaid: 'pill-gray' };
const STATUS_COLORS = { pending: 'pill-orange', approved: 'pill-green', rejected: 'pill-red' };

const EMPTY_FORM = { leave_type: 'casual', start_date: '', end_date: '', reason: '', employee_id: '' };

export default function LeavesPage() {
  const { user } = useAuth();
  const isHR = user?.role === 'owner' || user?.role === 'admin';
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const { data: leaves = [], isLoading } = useQuery({
    queryKey: ['hr-leaves', statusFilter],
    queryFn: () => api.get(`/hr/leaves${statusFilter ? `?status=${statusFilter}` : ''}`).then(r => r.data),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['hr-employees'],
    queryFn: () => api.get('/hr/employees').then(r => r.data),
    enabled: isHR,
  });

  const { data: myEmployee = [] } = useQuery({
    queryKey: ['hr-my-employee'],
    queryFn: () => api.get('/hr/employees').then(r => r.data),
    enabled: !isHR,
  });

  const myEmp = myEmployee[0];

  const { data: balance } = useQuery({
    queryKey: ['hr-leave-balance', myEmp?.id],
    queryFn: () => api.get(`/hr/leaves/balance/${myEmp.id}`).then(r => r.data),
    enabled: !isHR && !!myEmp?.id,
  });

  const submitMutation = useMutation({
    mutationFn: data => api.post('/hr/leaves', data),
    onSuccess: () => {
      qc.invalidateQueries(['hr-leaves']);
      qc.invalidateQueries(['hr-leave-balance']);
      toast.success('Leave request submitted');
      setShowForm(false);
      setForm(EMPTY_FORM);
    },
    onError: err => toast.error(err.response?.data?.error || 'Failed'),
  });

  const actionMutation = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/hr/leaves/${id}/status`, { status }),
    onSuccess: (_, { status }) => {
      qc.invalidateQueries(['hr-leaves']);
      toast.success(status === 'approved' ? 'Leave approved' : 'Leave rejected');
    },
    onError: err => toast.error(err.response?.data?.error || 'Failed'),
  });

  const cancelMutation = useMutation({
    mutationFn: id => api.delete(`/hr/leaves/${id}`),
    onSuccess: () => { qc.invalidateQueries(['hr-leaves']); toast.success('Request cancelled'); },
    onError: err => toast.error(err.response?.data?.error || 'Failed'),
  });

  const days = form.start_date && form.end_date
    ? Math.max(1, Math.ceil((new Date(form.end_date) - new Date(form.start_date)) / 86400000) + 1)
    : 0;

  if (isLoading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="section-head">
        <h2>Leave Management</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 150 }}>
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <button className="btn-primary" onClick={() => setShowForm(true)}>+ Apply Leave</button>
        </div>
      </div>

      {/* Leave balance (for team members) */}
      {!isHR && balance && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">Leave Balance — {new Date().getFullYear()}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
            {Object.entries(LEAVE_TYPES).map(([type, label]) => {
              const b = balance[type] || { allocated: 0, used: 0, remaining: 0 };
              return (
                <div key={type} style={{ padding: '10px 12px', background: 'var(--surface2)', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>{label}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 20, fontWeight: 800, color: type === 'unpaid' ? 'var(--text3)' : b.remaining > 0 ? 'var(--accent)' : 'var(--red)' }}>{type === 'unpaid' ? '∞' : b.remaining}</span>
                    {type !== 'unpaid' && <span style={{ fontSize: 11, color: 'var(--text2)' }}>/ {b.allocated} days</span>}
                  </div>
                  {type !== 'unpaid' && <div style={{ fontSize: 10, color: 'var(--text3)' }}>Used: {b.used} days</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Leaves table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                {isHR && <th>Employee</th>}
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Applied On</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leaves.map(lr => (
                <tr key={lr.id}>
                  {isHR && <td style={{ fontWeight: 500 }}>{lr.full_name}<div style={{ fontSize: 11, color: 'var(--text2)' }}>{lr.department}</div></td>}
                  <td><span className={`pill ${TYPE_COLORS[lr.leave_type] || 'pill-gray'}`}>{LEAVE_TYPES[lr.leave_type] || lr.leave_type}</span></td>
                  <td>{new Date(lr.start_date).toLocaleDateString('en-IN')}</td>
                  <td>{new Date(lr.end_date).toLocaleDateString('en-IN')}</td>
                  <td style={{ fontWeight: 600 }}>{lr.days}</td>
                  <td style={{ color: 'var(--text2)', fontSize: 12, maxWidth: 200 }}>{lr.reason || '—'}</td>
                  <td>
                    <span className={`pill ${STATUS_COLORS[lr.status] || 'pill-gray'}`}>{lr.status}</span>
                    {lr.approved_by_name && <div style={{ fontSize: 10, color: 'var(--text3)' }}>by {lr.approved_by_name}</div>}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text2)' }}>{new Date(lr.created_at).toLocaleDateString('en-IN')}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {isHR && lr.status === 'pending' && (
                        <>
                          <button className="btn btn-sm" style={{ color: 'var(--green)', borderColor: 'var(--green-light)' }}
                            onClick={() => actionMutation.mutate({ id: lr.id, status: 'approved' })}>Approve</button>
                          <button className="btn-danger btn-sm"
                            onClick={() => actionMutation.mutate({ id: lr.id, status: 'rejected' })}>Reject</button>
                        </>
                      )}
                      {lr.status === 'pending' && (
                        <button className="btn-danger btn-sm" onClick={() => { if (window.confirm('Cancel this request?')) cancelMutation.mutate(lr.id); }}>Cancel</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {leaves.length === 0 && <tr><td colSpan={isHR ? 9 : 8} className="empty">No leave requests found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Apply Leave modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">Apply Leave</div>
            <form onSubmit={e => { e.preventDefault(); submitMutation.mutate(form); }}>
              {isHR && (
                <div className="field">
                  <label>Employee *</label>
                  <select value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))} required>
                    <option value="">Select employee</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
                  </select>
                </div>
              )}
              <div className="field">
                <label>Leave Type *</label>
                <select value={form.leave_type} onChange={e => setForm(f => ({ ...f, leave_type: e.target.value }))}>
                  {Object.entries(LEAVE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="form-grid">
                <div className="field">
                  <label>From Date *</label>
                  <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} required />
                </div>
                <div className="field">
                  <label>To Date *</label>
                  <input type="date" value={form.end_date} min={form.start_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} required />
                </div>
              </div>
              {days > 0 && <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, marginBottom: 10 }}>{days} day{days > 1 ? 's' : ''}</div>}
              <div className="field">
                <label>Reason *</label>
                <textarea rows={3} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Why are you requesting this leave?" required />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitMutation.isPending}>Submit Request</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
